import React from 'react'

import { getKpiFormulaNode } from '../utils'

import Box from 'components_new/atoms/Box'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import KpiFormulaBlock from './KpiFormulaBlock'

import { KPI_UNIT } from 'types/Enums'

import { handleDeleteBlock, handleEditType, handleEditValue } from './utils'
import { TMetricCalculationBlock } from 'types/GlobalMetric'
import { ICustomEconomyMetric } from 'types/GlobalMetric'
import { KpiVariable, KpiVariableWithChildren } from 'types/GlobalKpiVariable'
import { useTranslation } from 'language/translator'

interface EditKpiSectionProps {
  formattedVariables: KpiVariableWithChildren[]
  editFormula: TMetricCalculationBlock
  editName: string
  editUnit: KPI_UNIT
  homepalKpi?: boolean
  setEditFormula: (value: any) => void
  setEditName: (value: string) => void
  setEditUnit?: (value: KPI_UNIT) => void
  validName: boolean
  variables: KpiVariable[]
  templates: ICustomEconomyMetric[]
}

const EditKpiSection = (props: EditKpiSectionProps) => {
  const {
    formattedVariables,
    editFormula,
    editName,
    editUnit,
    homepalKpi = false,
    setEditFormula,
    setEditName,
    setEditUnit,
    validName,
    variables,
    templates
  } = props

  const { t } = useTranslation()

  return (
    <>
      <Tooltip title={homepalKpi ? t('metrics', 'standard_metric_locked') : ''}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <TextField
            label={t('global', 'name')}
            disabled={homepalKpi}
            error={!validName}
            helperText={
              validName ? undefined : 'Ett mätetal med det namnet finns redan'
            }
            value={editName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEditName(event.target.value)
            }}
          />
          <TextField
            disabled={homepalKpi || !setEditUnit}
            label={t('global', 'unit')}
            select={true}
            sx={{ width: 96 }}
            value={editUnit}
          >
            {Object.values(KPI_UNIT).map((unitOption) => (
              <MenuItem
                value={unitOption}
                key={unitOption}
                onClick={() => (setEditUnit ? setEditUnit(unitOption) : {})}
                disabled={homepalKpi}
              >
                {unitOption}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Tooltip>
      <Box sx={{ mt: 2, minWidth: 0 }}>
        <SectionHeader title={t('global', 'formula')} />
        <Box sx={{ overflowX: 'auto', pt: 2 }}>
          <Box sx={{ minWidth: '100%', width: 'max-content' }}>
            <KpiFormulaBlock
              block={editFormula}
              formattedVariables={formattedVariables}
              editType={(id, side, value) => {
                const edited = handleEditType(editFormula, id, side, value)

                setEditFormula(edited)
              }}
              editValue={(id, key, value) => {
                const edited = handleEditValue(editFormula, id, key, value)

                setEditFormula(edited)
              }}
              deleteBlock={(id, side) => {
                const edited = handleDeleteBlock(editFormula, id, side)

                setEditFormula(edited)
              }}
              variables={variables}
              templates={templates}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 2, minWidth: 0 }}>
        <SectionHeader title={t('global', 'preview')} />
        <Box sx={{ overflowX: 'auto' }}>
          {editFormula ? (
            <>{getKpiFormulaNode(editFormula, variables, templates)}</>
          ) : (
            <EmptyPlaceholder title={t('global', 'formula_missing')} />
          )}
        </Box>
      </Box>
    </>
  )
}

export default EditKpiSection
